import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import {
    CapacitorSQLite,
    CapacitorSQLitePlugin, capSQLiteSet,
    capSQLiteUpgradeOptions, JsonSQLite,
    SQLiteConnection,
    SQLiteDBConnection
} from '@capacitor-community/sqlite';
import { environment } from '@env/environment';
import { DbUpgradeStatements, } from '@app/upgrades';
import { StorageService } from './storage.service';
import { App } from '@capacitor/app';

@Injectable()
export class SqliteService
{
    private isInitialized = false;
    private isNative = false;
    private sqliteConnection!: SQLiteConnection;
    private sqlitePlugin!: CapacitorSQLitePlugin;
    private dbName!: string;
    private db!: SQLiteDBConnection;
    private storageService = inject(StorageService);

    async initialize(): Promise<void>
    {
        if (this.isInitialized)
        {
            return;
        }

        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Initializing sqlite service',
            level: 'info',
        });

        try
        {
            this.isNative = Capacitor.isNativePlatform();
            this.dbName = environment.databaseName;
            this.sqlitePlugin = CapacitorSQLite;
            this.sqliteConnection = new SQLiteConnection(this.sqlitePlugin);

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Variables initialized',
                data: {
                    isNative: this.isNative,
                    dbName: this.dbName,
                },
                level: 'info',
            });

            await this.initializeDatabase();

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Database initialized',
                level: 'info',
            });

            const clearDatabaseKeyValue = await this.storageService.get<string>(this.storageService.clearDatabaseKey);
            const shouldClearDatabase = clearDatabaseKeyValue === null || clearDatabaseKeyValue !== environment.clearDatabaseKey;

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Check if should clear database',
                data: {
                    shouldClearDatabase,
                },
                level: 'info',
            });

            if (shouldClearDatabase)
            {
                Sentry.addBreadcrumb({
                    category: 'sqlite',
                    message: 'Deleting database',
                    level: 'info',
                });
                await this.deleteDatabase();
            }

            await this.storageService.set<string>(this.storageService.clearDatabaseKey, environment.clearDatabaseKey);

            await this.listenToAppLifecycle();

            this.isInitialized = true;

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Sqlite service initialized successfully',
                level: 'info',
            });
        }
        catch (error: any)
        {
            console.error('initialize', error);

            throw error;
        }
    }

    async closeConnection(isReadonly = false): Promise<void>
    {
        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Closing connection',
            data: {
                dbName: this.dbName,
                isReadonly,
            },
            level: 'info',
        });

        try
        {
            await this.sqliteConnection.closeConnection(this.dbName, isReadonly);

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Connection closed successfully',
                level: 'info',
            });
        }
        catch (error: any)
        {
            console.error('closeConnection', error);
            Sentry.captureException(error);

            throw error;
        }
    }

    async get(sql: string, values?: any[]): Promise<any[] | null>
    {
        try
        {
            const result = await this.db.query(sql, values);

            if (!result || !result.values || result.values.length === 0)
            {
                return null;
            }

            return result.values;
        }
        catch (error: any)
        {
            console.error('get', error);

            throw error;
        }
    }

    async exportToJSON(): Promise<JsonSQLite | null>
    {
        try
        {
            await this.openDatabase();

            const exportMode = 'full';

            const data = await this.db.exportToJson(exportMode);
            console.log('exportToJSON', data);

            if (!data || !data.export)
            {
                return null;
            }

            return data.export;
        }
        catch (error: any)
        {
            console.error('exportToJSON', error);

            throw error;
        }
    }

    async executeSet(queries: capSQLiteSet[]): Promise<any>
    {
        if (queries.length <= 0)
        {
            return null;
        }

        try
        {
            const result = await this.db.executeSet(queries);
            console.log('executeSet result', result);

            if (!result || !result.changes)
            {
                return null;
            }

            if (!result.changes.lastId)
            {
                return null;
            }

            return result.changes.lastId;
        }
        catch (error: any)
        {
            console.error('execute', error);

            throw error;
        }
    }

    async save(sql: string, params: any[] = []): Promise<number | null>
    {
        if (params.length <= 0)
        {
            return null;
        }

        try
        {
            const result = await this.db.run(sql, params);

            if (!result || !result.changes)
            {
                return null;
            }

            if (!result.changes.lastId)
            {
                return null;
            }

            return result.changes.lastId;
        }
        catch (error: any)
        {
            console.error('execute', error);

            throw error;
        }
    }

    async delete(sql: string, params: any[] = []): Promise<void>
    {
        try
        {
            const result = await this.db.run(sql, params);
        }
        catch (error: any)
        {
            console.error('delete', error);

            throw error;
        }
    }

    async deleteDatabase()
    {
        try
        {
            const { result } = await this.sqlitePlugin.isDBExists({ database: this.dbName, readonly: false, });

            if (result)
            {
                await this.sqlitePlugin.deleteDatabase({ database: this.dbName, readonly: false, });
            }

            // need to re-initialize the database
            await this.initializeDatabase();
        }
        catch (error: any)
        {
            console.error('deleteDatabase', error);

            throw error;
        }
    }

    getValuePlaceholder(rowsCount: number, fieldsCount: number): string
    {
        const singleRow = `(${Array(fieldsCount).fill('?').join(', ')})`;

        return Array(rowsCount).fill(singleRow).join(', ');
    }

    private async initializeDatabase()
    {
        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Initializing database',
            level: 'info',
        });

        if (!this.isNative)
        {
            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Initializing web store',
                level: 'info',
            });

            try
            {
                await this.sqliteConnection.initWebStore();
            }
            catch (error: any)
            {
                console.error('cant initWebStore', error);

                throw error;
            }
        }

        try
        {
            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Checking connection consistency',
                level: 'info',
            });

            const connectionConsistency = await this.sqliteConnection.checkConnectionsConsistency();
            console.log('connectionConsistency', connectionConsistency);

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Connection consistency checked',
                data: {
                    result: connectionConsistency.result,
                },
                level: 'info',
            });

            if (connectionConsistency.result)
            {
                Sentry.addBreadcrumb({
                    category: 'sqlite',
                    message: 'Closing existing connection',
                    level: 'info',
                });
                await this.closeConnection();
            }
        }
        catch (error: any)
        {
            console.error('cant check connection consistency', error);

            throw error;
        }

        try
        {
            const upgradeStatements = DbUpgradeStatements.getUpgradeStatements();
            const latestUpgradeVersion = upgradeStatements[upgradeStatements.length - 1].toVersion;

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Adding upgrade statements',
                data: {
                    latestUpgradeVersion,
                },
                level: 'info',
            });

            await this.addUpgradeStatement({
                database: this.dbName,
                upgrade: upgradeStatements,
            });

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Opening database',
                level: 'info',
            });

            await this.openDatabase(
                latestUpgradeVersion,
            );
        }
        catch (error: any)
        {
            console.error('initializeDatabase', error);

            throw error;
        }

        if (!this.isNative)
        {
            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Saving to web store',
                level: 'info',
            });

            try
            {
                await this.sqliteConnection.saveToStore(this.dbName);
            }
            catch (error: any)
            {
                console.error('cant saveToStore', error);

                throw error;
            }
        }

        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Database initialized successfully',
            level: 'info',
        });
    }

    private async addUpgradeStatement(options: capSQLiteUpgradeOptions): Promise<void>
    {
        try
        {
            await this.sqlitePlugin.addUpgradeStatement(options);
        }
        catch (error: any)
        {
            console.error('addUpgradeStatement', error);

            throw error;
        }
    }

    private async openDatabase(
        version: number | null = null,
        isEncrypted = false,
        mode: string = 'no-encryption',
        isReadonly = false,
    ): Promise<void>
    {
        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Opening database method called',
            data: {
                version,
                isEncrypted,
                mode,
                isReadonly,
                dbName: this.dbName,
            },
            level: 'info',
        });

        try
        {
            if (version === null)
            {
                const upgradeStatements = DbUpgradeStatements.getUpgradeStatements();
                version = upgradeStatements[upgradeStatements.length - 1].toVersion;

                Sentry.addBreadcrumb({
                    category: 'sqlite',
                    message: 'Version resolved from upgrade statements',
                    data: {
                        version,
                    },
                    level: 'info',
                });
            }

            let db: SQLiteDBConnection;

            const retCC = await this.sqliteConnection.checkConnectionsConsistency();
            const isConn = await this.sqliteConnection.isConnection(this.dbName, isReadonly);

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Checking connection state',
                data: {
                    consistencyResult: retCC?.result,
                    isConnection: isConn,
                },
                level: 'info',
            });

            if (!retCC || !isConn || !retCC.result)
            {
                Sentry.addBreadcrumb({
                    category: 'sqlite',
                    message: 'Creating new connection',
                    level: 'info',
                });
                db = await this.sqliteConnection.createConnection(this.dbName, isEncrypted, mode, version, isReadonly);
            }
            else
            {
                Sentry.addBreadcrumb({
                    category: 'sqlite',
                    message: 'Retrieving existing connection',
                    level: 'info',
                });
                db = await this.sqliteConnection.retrieveConnection(this.dbName, isReadonly);
            }

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Calling await this.db.isDBOpen()',
                level: 'info',
            });
            const isOpen = await db.isDBOpen();
            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'await this.db.isDBOpen() result',
                data: {
                    result: isOpen.result,
                },
                level: 'info',
            });

            if (!isOpen.result)
            {
                await db.open();
            }

            this.db = db;

            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'Database opened successfully',
                level: 'info',
            });
        }
        catch (error: any)
        {
            console.error('openDatabase', error);
            Sentry.captureException(error);

            throw error;
        }
    }

    private async listenToAppLifecycle(): Promise<void>
    {
        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Setting up listenToAppLifecycle',
            level: 'info',
        });

        await App.addListener('appStateChange', async ({ isActive }) =>
        {
            Sentry.addBreadcrumb({
                category: 'sqlite',
                message: 'appStateChange event received',
                data: {
                    isActive,
                },
                level: 'info',
            });

            if (isActive)
            {
                try
                {
                    await this.openDatabase();
                }
                catch (error: any)
                {
                    console.error('listenToAppLifecycle appStateChange', error);
                    Sentry.captureException(error);

                    throw error;
                }
            }
        });
    }
}
